"use client";

import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Sphere, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import ConditionalCanvas from "./ConditionalCanvas";

/**
 * Floating orb representing career opportunity.
 * Uses a distorted sphere for a dynamic, organic feel.
 */
function CareerOrb({ position, color, speed }: { position: [number, number, number], color: string, speed: number }) {
  const mesh = useRef<THREE.Mesh>(null!);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    mesh.current.rotation.y = time * speed * 0.3;
    mesh.current.rotation.x = time * speed * 0.1;
  });

  return (
    <Float speed={speed * 1.5} rotationIntensity={0.5} floatIntensity={1.5}>
      <Sphere ref={mesh} args={[1, 32, 32]} position={position}>
        <MeshDistortMaterial
          color={color}
          speed={speed * 2}
          distort={0.35}
          radius={1}
          emissive={color}
          emissiveIntensity={0.3}
          transparent
          opacity={0.5}
          wireframe
        />
      </Sphere>
    </Float>
  );
}

/**
 * Rising particles to symbolize growth and opportunity.
 * Uses simple geometry for performance optimization.
 */
function RisingParticles({ count = 80 }) {
  const particles = useRef<THREE.Points>(null!);
  
  const positions = useMemo(() => {
    const temp = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      temp[i * 3] = (Math.random() - 0.5) * 20;
      temp[i * 3 + 1] = (Math.random() - 0.5) * 15;
      temp[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return temp;
  }, [count]);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    const posArray = particles.current.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < count; i++) {
      posArray[i * 3 + 1] += 0.01; // Rise slowly
      if (posArray[i * 3 + 1] > 8) {
        posArray[i * 3 + 1] = -8;
      }
    }
    particles.current.geometry.attributes.position.needsUpdate = true;
    particles.current.rotation.y = time * 0.02;
  });

  return (
    <points ref={particles}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.06}
        color="#00f3ff"
        transparent
        opacity={0.8}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

/**
 * CareersHeroScene - A Three.js scene for the Careers page hero section.
 * Features floating orbs and rising particles symbolizing growth and opportunity.
 */
export default function CareersHeroScene() {
  return (
    <div className="absolute inset-0 z-0 bg-[#040d21]">
      <ConditionalCanvas camera={{ position: [0, 0, 12], fov: 50 }}>
        <ambientLight intensity={0.3} />
        <pointLight position={[5, 5, 5]} intensity={0.8} color="#00f3ff" />
        <pointLight position={[-5, -5, -5]} intensity={0.4} color="#FF7A00" />

        {/* Career Orbs */}
        <CareerOrb position={[-4, 1, 0]} color="#00f3ff" speed={0.8} />
        <CareerOrb position={[4, -1, -2]} color="#0066FF" speed={1.2} />
        <CareerOrb position={[0, 3, -4]} color="#FF7A00" speed={0.6} />

        {/* Rising Particles */}
        <RisingParticles count={100} />

        <fog attach="fog" args={['#040d21', 8, 22]} />
      </ConditionalCanvas>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#040d21]/50 via-transparent to-[#040d21]/70 pointer-events-none" />
    </div>
  );
}
